import React, { useEffect, useState } from "react";
import { Calendar, Send, CheckCircle2, Clock, XCircle } from "lucide-react";
import { toast } from "sonner";
import { portalApi } from "../../lib/portalApi";
import { Button } from "../../components/ui/button";
import { FormField } from "../../components/ui/form-field";
import { DatePickerUS } from "../../components/ui/date-picker-us";
import { to12h } from "../../lib/format";
import { formatApiError } from "../../lib/api";
import { useI18n } from "../../i18n/I18nContext";

const TIME_SLOTS = ["8h – 11h", "11h – 16h", "16h – 19h"];
const FREQUENCIES = ["Única vez", "Semanal", "Quinzenal", "Mensal"];

const STATUS_MAP = {
  pending: { label: "Em análise", cls: "bg-amber-50 text-amber-800 border-amber-200", Icon: Clock },
  approved: { label: "Aprovado", cls: "bg-emerald-50 text-emerald-800 border-emerald-200", Icon: CheckCircle2 },
  scheduled: { label: "Agendado", cls: "bg-blue-50 text-blue-800 border-blue-200", Icon: Calendar },
  rejected: { label: "Recusado", cls: "bg-rose-50 text-rose-800 border-rose-200", Icon: XCircle },
};

function fmtDate(iso) {
  if (!iso) return "—";
  // Accept both ISO date and date-only
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

export default function PortalBooking() {
  const { t } = useI18n();
  const [form, setForm] = useState({
    preferred_date: "",
    preferred_time: "",
    frequency: "",
    notes: "",
  });
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  async function load() {
    try {
      const { data } = await portalApi.get("/portal/bookings");
      setRequests(data);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  function upd(k, v) {
    setForm((p) => ({ ...p, [k]: v }));
  }

  async function submit(e) {
    e.preventDefault();
    if (!form.preferred_date) {
      toast.error(t("Selecione uma data preferida"));
      return;
    }
    setSaving(true);
    try {
      await portalApi.post("/portal/bookings", form);
      toast.success(t("Solicitação enviada! Vamos te responder em breve."));
      setForm({ preferred_date: "", preferred_time: "", frequency: "", notes: "" });
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t("Erro ao enviar solicitação"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6" data-testid="portal-booking">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">{t("Solicitar Agendamento")}</h1>
        <p className="text-slate-600 text-sm mt-1">
          {t("Escolha sua data preferida e envie a solicitação. Vamos confirmar em até 48h.")}
        </p>
      </div>

      <form
        onSubmit={submit}
        className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4"
        data-testid="portal-booking-form"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label={t("Data preferida")} required>
            <DatePickerUS
                testId="booking-date"
                value={form.preferred_date}
                onChange={(v) => upd("preferred_date", v)}
                min={new Date().toISOString().slice(0, 10)}
              />
          </FormField>
          <FormField label={t("Horário preferido")}>
            <select
              data-testid="booking-time"
              className="cm-input"
              value={form.preferred_time}
              onChange={(e) => upd("preferred_time", e.target.value)}
            >
              <option value="">{t("— sem preferência —")}</option>
              {TIME_SLOTS.map((s) => (
                <option key={s} value={s}>{t(s)}</option>
              ))}
            </select>
          </FormField>
          <FormField label={t("Frequência desejada")}>
            <select
              data-testid="booking-frequency"
              className="cm-input"
              value={form.frequency}
              onChange={(e) => upd("frequency", e.target.value)}
            >
              <option value="">{t("— a definir —")}</option>
              {FREQUENCIES.map((s) => (
                <option key={s} value={s}>{t(s)}</option>
              ))}
            </select>
          </FormField>
        </div>

        <FormField label={t("Observações (opcional)")} hint={t("Conte qualquer detalhe importante (cômodos extras, acesso, pets…)")}>
          <textarea
            data-testid="booking-notes"
            rows={4}
            className="cm-input"
            value={form.notes}
            onChange={(e) => upd("notes", e.target.value)}
          />
        </FormField>

        <div className="flex justify-end">
          <Button
            data-testid="booking-submit"
            type="submit"
            disabled={saving}
          >
            <Send size={15} className="mr-2" />
            {saving ? t("Enviando…") : t("Enviar solicitação")}
          </Button>
        </div>
      </form>

      <div>
        <h2 className="text-lg font-semibold text-slate-900 mb-3">{t("Suas solicitações")}</h2>
        {loading ? (
          <div className="text-slate-500 text-sm">{t("Carregando…")}</div>
        ) : requests.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-6 text-center text-slate-500 text-sm" data-testid="portal-booking-empty">
            {t("Você ainda não fez nenhuma solicitação.")}
          </div>
        ) : (
          <ul className="space-y-3">
            {requests.map((r) => {
              const st = STATUS_MAP[r.status] || STATUS_MAP.pending;
              const StIcon = st.Icon;
              return (
                <li
                  key={r.id}
                  data-testid={`booking-${r.id}`}
                  className="bg-white rounded-2xl border border-slate-200 p-4"
                >
                  <div className="flex flex-wrap items-start gap-3 justify-between">
                    <div className="space-y-1">
                      <div className="font-semibold text-slate-900 flex items-center gap-2">
                        <Calendar size={14} className="text-blue-600" />
                        {fmtDate(r.preferred_date)}
                        {r.preferred_time && <span className="text-sm text-slate-600 font-normal">• {to12h(r.preferred_time)}</span>}
                      </div>
                      {r.frequency && (
                        <div className="text-xs text-slate-500">{t("Frequência")}: {r.frequency}</div>
                      )}
                      {r.notes && (
                        <div className="text-xs text-slate-600 bg-slate-50 rounded p-2 mt-1 whitespace-pre-line">
                          {r.notes}
                        </div>
                      )}
                    </div>
                    <span className={`text-[11px] font-semibold px-2 py-1 rounded-full border inline-flex items-center gap-1 ${st.cls}`}>
                      <StIcon size={11} /> {t(st.label)}
                    </span>
                  </div>
                  {r.admin_notes && (
                    <div className="mt-2 text-xs text-blue-800 bg-blue-50 border border-blue-200 rounded p-2">
                      <strong>{t("Resposta:")}</strong> {r.admin_notes}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
